<template>
    <div v-if="closable && !modelValue" class="-mt-2 -mr-2 flex justify-end">
        <XLightIcon class="ml-3 h-3 w-3 cursor-pointer text-white" @click="close" />
    </div>

    <div
        ref="containerRef"
        class="relative flex h-full w-full flex-1 items-center justify-center text-center"
        :class="containerClasses"
        @click.stop="onClick"
        @focusin="showHide(true)"
        @focusout="showHide(false)"
        @mouseover="showHide(true)"
        @mouseleave="showHide(false)"
    >
        <slot />

        <InformationCircleIcon v-if="displayIcon && isInfo" class="h-4 w-4" :class="iconClasses" />
        <QuestionMarkCircleIcon
            v-else-if="displayIcon"
            class="ml-1 h-4 w-4"
            :class="`${iconClasses} ${link ? 'cursor-pointer' : ''}`"
            @click="openLink"
        />

        <transition v-if="$slots.tooltip" name="fade">
            <div
                v-show="isVisible"
                ref="tooltipRef"
                :class="[
                    resolvedBottomPosition ? 'bottom' : 'top',
                    mobileAlign === 'end' ? 'tooltip-mobile-end' : '',
                    tooltipClasses,
                ]"
                :style="tooltipStyle"
                class="tooltip-panel bg-dark-base border-border absolute z-50 w-full items-center justify-center rounded-lg border p-2 text-center text-sm text-white shadow-xl shadow-black/40"
            >
                <span
                    :class="[
                        resolvedBottomPosition ? 'triangle-bottom' : 'triangle-top',
                        mobileAlign === 'end' ? 'tooltip-triangle-mobile-end' : '',
                    ]"
                    :style="arrowStyle"
                ></span>
                <slot name="tooltip"></slot>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref } from 'vue'

import { InformationCircleIcon, QuestionMarkCircleIcon } from '@heroicons/vue/24/solid'

import XLightIcon from '@/components/icons/XLightIcon.vue'
import { fitTooltipHorizontally, resolveTooltipPlacement } from '@/lib/ui/tooltip-position'

defineOptions({ name: 'AppTooltip' })

const emits = defineEmits(['close'])
const props = defineProps({
    duration: { type: Number, default: 3000 },
    fadeOut: { type: Number, default: 500 },
    bottomPosition: { type: Boolean, default: false },
    closable: { type: Boolean, default: false },
    modelValue: { type: Boolean, default: false },
    displayIcon: { type: Boolean, default: true },
    isInfo: { type: Boolean, default: false },
    link: { type: String, default: null },
    iconClasses: { type: String, default: '' },
    containerClasses: { type: String, default: '' },
    tooltipClasses: { type: String, default: '' },
    mobileAlign: {
        type: String,
        default: 'center',
        validator: (value) => ['center', 'end'].includes(value),
    },
})

const isVisible = ref(false)
const containerRef = ref(null)
const tooltipRef = ref(null)
const positionedLeft = ref(null)
const positionedArrowLeft = ref(null)
const positionedMaxHeight = ref(null)
const resolvedBottomPosition = ref(props.bottomPosition)

const tooltipStyle = computed(() =>
    positionedLeft.value === null
        ? undefined
        : {
              left: `${positionedLeft.value}px`,
              right: 'auto',
              transform: 'none',
              maxHeight: `${positionedMaxHeight.value}px`,
          }
)
const arrowStyle = computed(() =>
    positionedArrowLeft.value === null
        ? undefined
        : {
              left: `${positionedArrowLeft.value}px`,
              right: 'auto',
              transform: resolvedBottomPosition.value ? 'translateX(-50%)' : 'translateX(-50%) rotate(180deg)',
          }
)

const clearPosition = () => {
    positionedLeft.value = null
    positionedArrowLeft.value = null
    positionedMaxHeight.value = null
    resolvedBottomPosition.value = props.bottomPosition
}

const positionWithinViewport = () => {
    const container = containerRef.value
    const tooltip = tooltipRef.value

    if (!isVisible.value || !container || !tooltip) return

    clearPosition()

    const measureAndPosition = () => {
        if (!isVisible.value || !containerRef.value || !tooltipRef.value) return

        const containerRect = containerRef.value.getBoundingClientRect()
        const tooltipRect = tooltipRef.value.getBoundingClientRect()
        const visualViewport = window.visualViewport
        const viewportLeft = visualViewport?.offsetLeft ?? 0
        const viewportWidth = visualViewport?.width ?? window.innerWidth
        const viewportTop = visualViewport?.offsetTop ?? 0
        const viewportHeight = visualViewport?.height ?? window.innerHeight
        const placement = resolveTooltipPlacement({
            preferredPlacement: props.bottomPosition ? 'bottom' : 'top',
            tooltipHeight: tooltipRect.height,
            anchorTop: containerRect.top,
            anchorBottom: containerRect.bottom,
            viewportTop,
            viewportHeight,
        })

        if (resolvedBottomPosition.value !== (placement === 'bottom')) {
            resolvedBottomPosition.value = placement === 'bottom'
            nextTick(measureAndPosition)
            return
        }

        const fitted = fitTooltipHorizontally({
            preferredLeft: tooltipRect.left,
            tooltipWidth: tooltipRect.width,
            anchorCenter: containerRect.left + containerRect.width / 2,
            viewportLeft,
            viewportWidth,
        })

        positionedLeft.value = fitted.left - containerRect.left
        positionedArrowLeft.value = fitted.arrowLeft
        positionedMaxHeight.value = Math.max(
            0,
            placement === 'bottom'
                ? viewportTop + viewportHeight - 16 - containerRect.bottom - 12
                : containerRect.top - viewportTop - 16 - 12
        )
    }

    nextTick(measureAndPosition)
}

const onViewportScroll = (event) => {
    const target = event.target

    if (target instanceof Node && tooltipRef.value?.contains(target)) return

    positionWithinViewport()
}

const startPositionTracking = () => {
    window.addEventListener('resize', positionWithinViewport)
    window.addEventListener('scroll', onViewportScroll, true)
    window.visualViewport?.addEventListener('resize', positionWithinViewport)
    window.visualViewport?.addEventListener('scroll', onViewportScroll)
}

const stopPositionTracking = () => {
    window.removeEventListener('resize', positionWithinViewport)
    window.removeEventListener('scroll', onViewportScroll, true)
    window.visualViewport?.removeEventListener('resize', positionWithinViewport)
    window.visualViewport?.removeEventListener('scroll', onViewportScroll)
}

let timerOnHide
const showHide = (show) => {
    if (timerOnHide) {
        clearTimeout(timerOnHide)
    }
    if (show) {
        const wasVisible = isVisible.value
        isVisible.value = true
        if (!wasVisible) {
            positionWithinViewport()
            startPositionTracking()
        }
    } else {
        timerOnHide = setTimeout(() => {
            isVisible.value = false
            clearPosition()
            stopPositionTracking()
        }, props.fadeOut)
    }
}

let timerOnClick
const onClick = () => {
    showHide(true)
    if (timerOnClick) {
        clearTimeout(timerOnClick)
    }
    timerOnClick = setTimeout(() => showHide(false), props.duration)
}

const close = () => emits('close')

const openLink = () => (props.link ? window.open(props.link, '_blank') : false)

onBeforeUnmount(() => {
    clearTimeout(timerOnHide)
    clearTimeout(timerOnClick)
    stopPositionTracking()
})
</script>

<style scoped>
.tooltip-panel {
    max-width: calc(100vw - 2rem);
    overflow-y: auto;
    overflow-wrap: anywhere;
    white-space: normal;
}

@media (max-width: 639px) {
    .tooltip-mobile-end {
        right: 0;
        left: auto;
        transform: none;
    }

    .tooltip-triangle-mobile-end {
        right: 8px;
        left: auto;
    }

    .triangle-top.tooltip-triangle-mobile-end {
        transform: rotate(180deg);
    }
}
</style>
